<template>
  <block
    component-name="block-carousel-accent"
    ref="tpl_root"
    body-class="grid grid-cols-12 w-full h-full bg-dynamic tablet:h-screen transition-colors duration-500 ease-in-out"
    :style="`--color-fill-dynamic: ${accentColor || '#000'};`"
    :px="false"
    body-skin="black"
  >
    <template v-slot:default="blockData">
      <!-- BOX 1 -->
      <div
        class="col-span-full tablet:col-span-6 laptop:col-span-4 flex flex-col justify-between gap-y-3xl px-edge py-3xl min-h-[50vh] tablet:max-h-screen text-skin-base"
      >
        <div class="w-full grow">
          <header
            class="relative top-0 left-0 flex flex-col tablet:sticky tablet:top-30 laptop:top-40 gap-lg"
          >
            <wrapper-reveal
              v-if="$attrs.data.body.overline"
              reveal="fade-from-right"
            >
              <div
                class="text-overline-big"
                v-html="$attrs.data.body.overline"
              ></div>
            </wrapper-reveal>

            <wrapper-split-text reveal="chars">
              <div class="text-heading-4" v-html="$attrs.data.body.title"></div>
            </wrapper-split-text>

            <wrapper-reveal
              v-if="$attrs.data.body.text"
              reveal="fade-from-bottom"
            >
              <div class="text-body-big" v-html="$attrs.data.body.text"></div>
            </wrapper-reveal>
          </header>
        </div>

        <wrapper-reveal
          v-if="$attrs.data.body.button"
          class="self-start min-h-min"
          reveal="fade-from-right"
        >
          <btn-pill
            :title="$attrs.data.body.button.title"
            :action="$attrs.data.body.button.action"
            icon="arrow-long-right"
          />
        </wrapper-reveal>
      </div>
      <!-- end BOX 1 -->

      <!-- BOX 2 -->
      <wrapper-reveal
        reveal="unveil-from-left"
        class="overflow-hidden col-span-full tablet:col-span-6 laptop:col-span-6 relative top-0 left-0 h-[50vh] tablet:h-screen tablet:max-h-screen w-full text-skin-base"
        :class="`landscape:[&_*::part(pagination)]:invisible 
          laptop:[&_*::part(pagination)]:!invisible 
          [&_*::part(pagination)]:right-edge
          [&_*::part(pagination)]:bottom-edge 
          [&_*::part(pagination)]:left-auto 
          [&_*::part(pagination)]:!m-0 
          [&_*::part(pagination)]:!p-0 
          [&_*::part(pagination)]:h-[22px] 
          [&_*::part(pagination)]:max-w-[76px] 
          [&_*::part(bullet)]:bg-skin-inverse 
          [&_*::part(bullet)]:opacity-50 
          [&_*::part(bullet)]:scale-100
          [&_*::part(bullet-active)]:bg-skin-inverse
        `"
      >
        <swiper-container
          init="false"
          class="w-full h-full"
          events-prefix="swiper-"
        >
          <swiper-slide
            v-for="(item, index) in $attrs.data.body.items"
            class="relative top-0 left-0 overflow-hidden bg-black"
          >
            <div
              class="absolute inset-0 w-full h-full max-h-full"
              data-swiper-parallax-scale="1.15"
              data-swiper-parallax-x="40%"
              data-swiper-parallax-opacity="0.5"
            >
              <media
                class="w-full h-full"
                v-bind="item.asset"
                :sizes="{
                  base: 600,
                  phablet: 1020,
                  tablet: 636,
                  laptop: 800,
                  desktop: 1000,
                  extdesktop: 1300,
                }"
                :loading="
                  index > 2 || blockData?.loading === 'lazy' ? 'lazy' : 'eager'
                "
              />
            </div>

            <div
              class="absolute inset-0 flex items-end justify-start w-full h-full z-2 overlay-gradient-from-bottom"
            ></div>

            <div
              data-swiper-parallax-x="-40%"
              data-swiper-parallax-opacity="0"
              class="absolute inset-0 flex items-end justify-start w-full h-full laptop:invisible z-3 px-edge py-3xl"
            >
              <wrapper-reveal
                v-if="item.text"
                reveal="fade-from-bottom"
                class="laptop:hidden grow w-full max-w-[450px]"
              >
                <div
                  class="w-full text-skin-base text-body"
                  v-html="item.text"
                ></div>
              </wrapper-reveal>
              <!-- PLACEHOLDER SPACER -->
              <div
                class="!invisible overflow-hidden flex laptop:hidden gap-lg pl-edge min-w-min"
                tab-index="-1"
                aria-hidden="true"
              >
                <div>
                  <btn-arrow
                    :disabled="true"
                    direction="prev"
                    @click="handleCarouselProgress('prev')"
                  />
                </div>

                <div>
                  <btn-arrow
                    :disabled="true"
                    direction="prev"
                    @click="handleCarouselProgress('next')"
                  />
                </div>
              </div>
              <!-- end PLACEHOLDER SPACER -->
            </div>
          </swiper-slide>
        </swiper-container>

        <div
          v-if="$attrs.data.body.items.length > 1"
          class="portrait:hidden flex laptop:!hidden absolute inset-0 items-end justify-end w-full h-full pointer-events-none z-4 gap-lg pr-edge pb-3xl"
        >
          <wrapper-reveal class="pointer-events-auto" reveal="fade-from-right">
            <btn-arrow
              :disabled="currentIndex === 0"
              direction="prev"
              @click="handleCarouselProgress('prev')"
            />
          </wrapper-reveal>

          <wrapper-reveal class="pointer-events-auto" reveal="fade-from-left">
            <btn-arrow
              :disabled="currentIndex === $attrs.data.body.items.length - 1"
              direction="next"
              @click="handleCarouselProgress('next')"
            />
          </wrapper-reveal>
        </div>
      </wrapper-reveal>
      <!-- end BOX 2 -->

      <!-- BOX 3 -->
      <div
        class="hidden laptop:flex flex-col items-start justify-end col-span-2 w-full h-full gap-2xl pl-2xl pr-edge py-3xl text-skin-base"
      >
        <div class="flex flex-col justify-end w-full grow">
          <wrapper-reveal
            reveal="fade-from-bottom"
            class="w-full pointer-events-auto"
          >
            <swiper-container
              :id="tinyDescId"
              init="false"
              class="w-full h-full"
              events-prefix="swiper-"
            >
              <swiper-slide
                v-for="item in $attrs.data.body.items"
                class="relative top-0 left-0 overflow-hidden"
              >
                <div
                  data-swiper-parallax-opacity="0"
                  class="flex items-end justify-start w-full h-full"
                >
                  <div
                    class="w-full max-w-[450px] grow text-skin-base text-body"
                    v-html="item.text"
                  ></div>
                </div>
              </swiper-slide>
            </swiper-container>
          </wrapper-reveal>
        </div>

        <div
          v-if="$attrs.data.body.items.length > 1"
          class="flex justify-start gap-lg min-h-min"
        >
          <wrapper-reveal reveal="fade-from-right">
            <btn-arrow
              :disabled="currentIndex === 0"
              direction="prev"
              @click="handleCarouselProgress('prev')"
            />
          </wrapper-reveal>

          <wrapper-reveal reveal="fade-from-left">
            <btn-arrow
              :disabled="currentIndex === $attrs.data.body.items.length - 1"
              direction="next"
              @click="handleCarouselProgress('next')"
            />
          </wrapper-reveal>
        </div>
      </div>
      <!-- end BOX 3 -->
    </template>
  </block>
</template>

<script setup>
const tinyDescId = ref(`block-carousel-accent_tiny-desc_${useId()}`);
const { setLuminance } = useColors();
const tpl_root = templateRef("tpl_root");
const attrs = useAttrs();
const currentIndex = ref(0);
const accentColor = computed(() => {
  return setLuminance(
    attrs.data.body.items[currentIndex.value].asset.dominant_color
  );
});

let swiperEl;

function handleCarouselProgress(nextPrev) {
  nextPrev == "prev"
    ? swiperEl[0]?.swiper.slidePrev()
    : swiperEl[0]?.swiper.slideNext();
}

useSafeMountedEl(tpl_root, () => {
  swiperEl = tpl_root.value.$el.querySelectorAll("swiper-container");

  Object.assign(swiperEl[1], {
    slidesPerView: 1,
    grabCursor: false,
    parallax: true,
    allowTouchMove: false,
    speed: 450,
    on: {
      init() {
        ScrollTrigger.refresh(true);
      },
    },
  });

  Object.assign(swiperEl[0], {
    slidesPerView: 1,
    grabCursor: true,
    parallax: true,
    speed: 650,
    pagination:
      attrs.data.body.items.length > 1 ? { dynamicBullets: true } : false,
    on: {
      init() {
        ScrollTrigger.refresh(true);
      },
      slideChange(swiper) {
        currentIndex.value = swiper.realIndex;
      },
    },
  });

  swiperEl[1].initialize();
  swiperEl[0].initialize();

  // controller must be assigned after initialisation due a Swiper malfunction
  // (declaring it as initialisation params is not working)
  swiperEl[0].swiper.controller.control = swiperEl[1].swiper;
});

onBeforeUnmount(() => {
  if (swiperEl) {
    swiperEl[0]?.swiper?.destroy();
    swiperEl[1]?.swiper?.destroy();
    swiperEl = null;
  }
});
</script>
